"use client";

import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { Box, Button, Card, Flex, Grid, Separator } from "@radix-ui/themes";
import DraftPagesWidget from "./DraftPagesWidget";
import { ContentItemApprovalWidget } from "./ContentItemApprovalWidget";
import { PublicInfo } from "../UserSettings/PublicInfo";
import { NotificationSettings } from "../UserSettings/NotificationSettings";
import FollowingView from "../../components/schoolfeed/FollowingView";
import { useStores } from "../../stores/useStores";
import styles from "../styles/userSettingsPageStyles.module.scss";
import { blockObjectFactory } from "../../components/blocks/SchoolBlocks/blockObjectFactory";

type ITab =
  | "#content-approval-tab"
  | "#draft-pages-tab"
  | "#notifications-tab"
  | "#subscriptions-tab"
  | "#account-tab";

const UserDashboardPage = observer(function UserDashboardPage() {
  const { userStore, organizationStore } = useStores();

  const navItems = (
    [
      { id: "#notifications-tab", label: "Notifications", show: true },
      { id: "#subscriptions-tab", label: "Subscriptions", show: true },
      { id: "#account-tab", label: "Account", show: true },
      {
        id: "#content-approval-tab",
        label: "Content Approval Requests",
        show: userStore.isEditor,
      },
      {
        id: "#draft-pages-tab",
        label: "Draft Pages",
        show: userStore.isEditor,
      },
    ] as { id: ITab; label: string; show: boolean }[]
  ).filter((item) => item.show);

  const [activeTab, setActiveTab] = useState<ITab>("#notifications-tab");

  useEffect(() => {
    const hashTab = navItems.find(
      (item) => item.id === window.location.hash,
    )?.id;
    if (hashTab) {
      setActiveTab(hashTab);
    }
  }, []);

  function selectTab(tab: ITab) {
    setActiveTab(tab);
    history.replaceState(null, "", tab);
  }

  const userObj = useMemo(
    () =>
      blockObjectFactory("person", {
        blockModel: organizationStore.currentOrganization.user as IUser,
      } as IPersonBlock) as IPersonBlock,
    [organizationStore.currentOrganization.user],
  );

  const content = {
    "#content-approval-tab": <ContentItemApprovalWidget />,
    "#draft-pages-tab": <DraftPagesWidget />,
    "#notifications-tab": (
      <div className={styles.notificationSettings}>
        <NotificationSettings
          setCurrentView={() => selectTab("#subscriptions-tab")}
        />
      </div>
    ),
    "#subscriptions-tab": <FollowingView />,
    "#account-tab": (
      <div className={styles.notificationSettings}>
        <PublicInfo userObj={userObj} />
      </div>
    ),
  }[activeTab];

  return (
    <Box id="sb-main-content" role="main">
      <Card size="3">
        <Grid columns={{ initial: "1fr", sm: "200px auto 1fr" }} gap="4">
          <Flex
            direction="column"
            gap="1"
            role="tablist"
            aria-orientation="vertical"
          >
            {navItems.map((item) => (
              <Button
                key={item.id}
                asChild
                variant="ghost"
                size={"3"}
                color={activeTab === item.id ? undefined : "gray"}
                style={{
                  justifyContent: "flex-start",
                  textAlign: "left",
                  boxSizing: "border-box",
                  width: "100%",
                  margin: 0,
                  backgroundColor:
                    activeTab === item.id ? "var(--accent-a3)" : undefined,
                }}
              >
                <a
                  href={item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    selectTab(item.id);
                  }}
                  role="tab"
                  aria-selected={activeTab === item.id}
                >
                  {item.label}
                </a>
              </Button>
            ))}
          </Flex>
          <Separator
            orientation={{ initial: "horizontal", sm: "vertical" }}
            size="4"
            style={{ backgroundColor: "var(--gray-6)" }}
          />
          <Box width={"100%"}>{content}</Box>
        </Grid>
      </Card>
    </Box>
  );
});

export default UserDashboardPage;
